/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from 'react';
import { useDebounce } from '@clover-util/use-debounce';
import { isFunction } from '@clover-util/is-function';
function tryInvoke(fn) {
    try {
        return fn();
    }
    catch (e) {
        console.error(e);
    }
}
function normalizeInitialState(initialState) {
    return isFunction(initialState) ? initialState() : initialState;
}
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function createStorageStateHook(provider, serializer) {
    const { serialize, deserialize } = serializer;
    const storage = tryInvoke(provider);
    function useStorageState(key, initialState) {
        const [hasLoaded, setHasLoaded] = useState(false);
        const getValueOrDefault = useCallback(() => {
            if (!key)
                return normalizeInitialState(initialState);
            const item = deserialize(storage === null || storage === void 0 ? void 0 : storage.getItem(key));
            setHasLoaded(true);
            if (item === null || item === undefined) {
                storage === null || storage === void 0 ? void 0 : storage.removeItem(key);
                return normalizeInitialState(initialState);
            }
            return item;
            // we don't want the initializer to cause this effect to run
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [key]);
        const [value, setValue] = useState(getValueOrDefault);
        // read from storage on key change effect
        useEffect(() => {
            if (!key)
                return;
            setHasLoaded(true);
            setValue(currentValue => {
                const serialized = serialize(currentValue);
                const item = storage === null || storage === void 0 ? void 0 : storage.getItem(key);
                if (serialized === item)
                    return currentValue;
                return getValueOrDefault();
            });
        }, [key, getValueOrDefault]);
        // effect to bind window storage event listener
        useEffect(() => {
            if (!hasLoaded)
                return;
            const onStorageEvent = (event) => {
                if (key !== event.key || event.newValue === event.oldValue)
                    return;
                if (event.newValue) {
                    const newValue = deserialize(event.newValue);
                    if (newValue !== undefined)
                        setValue(newValue);
                    return;
                }
                setValue(normalizeInitialState(initialState));
            };
            window.addEventListener('storage', onStorageEvent);
            return () => {
                window.removeEventListener('storage', onStorageEvent);
            };
            // we don't want the initializer to cause this effect to run
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [hasLoaded, key]);
        // throttle the updates, storage is slow
        const updater = useDebounce((storageKey, newValue) => {
            if (!storage)
                return;
            if (newValue === undefined) {
                storage.removeItem(storageKey);
            }
            else {
                storage.setItem(storageKey, serialize(newValue));
            }
        }, []);
        // effect to update value in storage
        useEffect(() => {
            if (!key || !hasLoaded)
                return;
            updater(key, value);
        }, [hasLoaded, key, updater, value]);
        return [value, setValue];
    }
    return useStorageState;
}
//# sourceMappingURL=createStorageStateHook.js.map